import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private static readonly scriptId = 'google-analytics-gtag';
  private initialized = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  initialize(): void {
    const measurementId = environment.googleAnalyticsMeasurementId?.trim();
    if (!measurementId || this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function (): void {
      // gtag.js requires the Arguments object used by Google's official snippet.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

    if (!this.document.getElementById(GoogleAnalyticsService.scriptId)) {
      const script = this.document.createElement('script');
      script.id = GoogleAnalyticsService.scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      this.document.head.appendChild(script);
    }

    window.gtag('js', new Date());
    window.gtag('config', measurementId, { send_page_view: false });
  }

  trackPageView(routerUrl: string): void {
    if (!this.initialized || !window.gtag) {
      return;
    }

    const route = this.normalizeRoute(routerUrl);
    const location = this.document.location;
    const pageLocation = `${location.origin}${location.pathname}#${route}`;

    window.gtag('event', 'page_view', {
      page_title: this.document.title,
      page_location: pageLocation,
    });
  }

  private normalizeRoute(routerUrl: string): string {
    const path = String(routerUrl || '/')
      .split('?')[0]
      .split('#')[0];

    return path.startsWith('/') ? path : `/${path}`;
  }
}
